import React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle, Activity, Scale, TrendingDown, CalendarClock, ChevronRight, Sparkles,
} from "lucide-react";

/**
 * FocusStrip — compact executive lead-line above the entity grid.
 *
 * The single "look here first" element on HQ home. Composes the SAME
 * canonical attention data already used by the Right Now drawer so we
 * never duplicate priority logic.
 *
 * Priority order (highest → lowest):
 *   1. Overdue High/Critical task
 *   2. Overdue project (from project_health_warnings)
 *   3. At Risk project
 *   4. Unresolved decision
 *   5. Budget warning (over-budget preferred)
 *   6. Nearest upcoming deadline
 *
 * Deduplication: an At-Risk/Overdue project that already has a matching
 * budget-warning row is preferred over the standalone warning — so a
 * single underlying issue never surfaces twice in Focus + counts.
 *
 * Deep link: computeLead attaches `to` — the canonical destination URL
 * for that lead (entity workspace tab + `focus=<record_id>`). The strip
 * exposes TWO independent tap targets so the executive can jump directly
 * to the specific record OR open Right Now for the full list:
 *
 *   [Focus · <lead title>  →]     ←—— deep-links to lead.to
 *   [ counts · Right Now → ]      ←—— opens the drawer
 *
 * Fallback: if a lead has no resolvable `to` (missing entity_id / id, or
 * the type is a pure entity-level warning), the lead region falls back
 * to the drawer so we never navigate to a broken route.
 *
 * Team Access: /api/dashboard/attention is already scoped server-side
 * (probe.py / probe10.py). Every entity_id/project_id we hand to Link
 * has ALREADY passed that filter, so deep-linking cannot leak a hidden
 * record or route.
 *
 * Renders "Nothing pressing." when every canonical bucket is empty.
 * Zero-value counts are never displayed.
 */
export default function FocusStrip({ data, onOpen, entities = [] }) {
  const overdue = data?.overdue_tasks || [];
  const upcoming = data?.upcoming_events || [];
  const decisions = data?.pending_decisions || [];
  const entityWarnings = data?.budget_warnings || [];
  const projectWarnings = data?.project_budget_warnings || [];
  const healthWarnings = data?.project_health_warnings || [];

  const healthBudgetProjectIds = new Set(
    healthWarnings
      .filter((h) => (h.reasons || []).some((r) => (r.code || "").startsWith("budget_")))
      .map((h) => h.project_id),
  );
  const dedupedProjectWarnings = projectWarnings.filter((w) => !healthBudgetProjectIds.has(w.project_id));
  const warningsTotal = entityWarnings.length + dedupedProjectWarnings.length;

  const overdueProjects = healthWarnings.filter((h) => (h.status || "") === "Overdue");
  const atRiskProjects = healthWarnings.filter((h) => (h.status || "") === "At Risk");
  const atRiskTotal = overdueProjects.length + atRiskProjects.length;
  const totalIssues = overdue.length + atRiskTotal + decisions.length + warningsTotal;

  const counts = [
    overdue.length > 0 && { key: "overdue", label: "overdue", n: overdue.length, color: "#EC4899" },
    atRiskTotal > 0 && { key: "risk", label: "at risk", n: atRiskTotal, color: "#EF4444" },
    decisions.length > 0 && { key: "dec", label: decisions.length === 1 ? "decision" : "decisions", n: decisions.length, color: "#F59E0B" },
    warningsTotal > 0 && { key: "warn", label: warningsTotal === 1 ? "budget warning" : "budget warnings", n: warningsTotal, color: "#F97316" },
    upcoming.length > 0 && { key: "up", label: "upcoming", n: upcoming.length, color: "#3B82F6" },
  ].filter(Boolean);

  const entityName = (eid) => entities.find((e) => e.id === eid)?.name;
  const lead = computeLead({
    overdue, overdueProjects, atRiskProjects, decisions,
    entityWarnings, dedupedProjectWarnings, upcoming, entityName,
  });
  const empty = totalIssues === 0 && upcoming.length === 0;

  const accent = lead ? lead.color : (empty ? "rgba(16,185,129,0.6)" : "rgba(255,255,255,0.15)");

  return (
    <div
      data-testid="focus-strip"
      data-focus-empty={empty ? "true" : "false"}
      className="w-full group relative overflow-hidden rounded-xl border border-white/10 bg-[#151A23] hover:border-white/20 transition-colors flex items-stretch"
    >
      {/* accent stripe reflects severity */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[3px] pointer-events-none"
        style={{ backgroundColor: accent }}
      />

      {/* Lead region — deep-links to the canonical record. */}
      <LeadRegion lead={lead} onOpen={onOpen} />

      {/* Counts + Right Now — always opens the drawer. */}
      <button
        type="button"
        onClick={onOpen}
        data-testid="focus-open-drawer"
        aria-label="Open Right Now"
        className="flex-shrink-0 flex items-center gap-3 md:gap-4 pl-2 pr-3 md:pl-3 md:pr-4 py-3 md:py-3.5 border-l border-white/[0.06] hover:bg-white/[0.03] transition-colors focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-inset"
      >
        {counts.length > 0 && (
          <div className="hidden md:flex items-center gap-x-3 gap-y-1 flex-wrap text-[11px] md:text-xs text-white/55 max-w-[360px]">
            {counts.map((c, i) => (
              <span key={c.key} className="inline-flex items-baseline gap-1" data-testid={`focus-count-${c.key}`}>
                <span className="tabular-nums font-semibold" style={{ color: c.color }}>{c.n}</span>
                <span className="text-white/50">{c.label}</span>
                {i < counts.length - 1 && <span className="text-white/20 ml-1">·</span>}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5 text-[10px] md:text-[11px] uppercase tracking-wider text-white/45 group-hover:text-white/80 transition-colors">
          <span className="hidden sm:inline">Right Now</span>
          <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </button>
    </div>
  );
}

function LeadRegion({ lead, onOpen }) {
  const empty = !lead;
  const Icon = lead?.Icon || Sparkles;
  const color = lead?.color || "rgba(16,185,129,0.9)";

  const inner = (
    <div className="pl-4 pr-3 md:pl-5 md:pr-4 py-3 md:py-3.5 flex items-center gap-3 md:gap-4 min-w-0 w-full">
      <div className="flex items-center gap-2.5 flex-shrink-0">
        <Icon className="w-4 h-4 md:w-[18px] md:h-[18px]" style={{ color }} />
        <span className="text-[9px] md:text-[10px] uppercase tracking-widest text-white/45 font-semibold hidden md:inline">
          Focus
        </span>
      </div>
      <div className="min-w-0 flex-1">
        {lead ? (
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm md:text-[15px] font-medium text-white leading-snug break-words">
              {lead.title}
            </span>
            {lead.tag && (
              <span
                className="text-[9px] md:text-[10px] uppercase tracking-wider font-medium flex-shrink-0"
                style={{ color }}
              >
                · {lead.tag}
              </span>
            )}
          </div>
        ) : (
          <span className="text-sm md:text-[15px] text-white/60 leading-snug">Nothing pressing.</span>
        )}
      </div>
      {/* Deep-link cue — only shown when the lead resolves to a canonical route. */}
      {lead?.to && (
        <ChevronRight
          className="w-4 h-4 flex-shrink-0 text-white/30 group-hover:text-white/70 transition-colors"
          data-testid="focus-lead-arrow"
        />
      )}
    </div>
  );

  const commonClass =
    "flex-1 min-w-0 text-left flex items-stretch focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-inset";

  if (lead?.to) {
    return (
      <Link
        to={lead.to}
        data-testid="focus-lead"
        data-lead-kind={lead.kind}
        data-lead-target={lead.to}
        className={`${commonClass} hover:bg-white/[0.03] transition-colors`}
      >
        {inner}
      </Link>
    );
  }
  // Fallback: no resolvable target OR empty state — clicking the lead region
  // opens Right Now instead of navigating to a broken route.
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="focus-lead"
      data-lead-kind={lead?.kind || "empty"}
      data-lead-target="fallback:drawer"
      aria-label={lead ? `Open Right Now (${lead.kind})` : "Nothing pressing"}
      className={`${commonClass} ${empty ? "cursor-default" : "hover:bg-white/[0.03] transition-colors"}`}
      disabled={empty}
    >
      {inner}
    </button>
  );
}

/**
 * computeLead — returns {title, tag, color, Icon, kind, to} for the single
 * highest-priority attention item.  `to` is a canonical react-router path
 * that deep-links the executive straight to the record — see mapping table:
 *
 *   overdue task        → /entity/{entity_id}?tab=tasks&focus={task_id}
 *   overdue project     → /entity/{entity_id}?tab=projects&focus={project_id}
 *   at-risk project     → /entity/{entity_id}?tab=projects&focus={project_id}
 *   decision            → /entity/{entity_id}?tab=notes&focus={note_id}
 *   budget (project)    → /entity/{entity_id}?tab=budget&focus={project_id}
 *   budget (entity)     → /entity/{entity_id}?tab=budget
 *   upcoming event      → /entity/{entity_id}?tab=calendar&focus={event_id}
 *   upcoming task       → /entity/{entity_id}?tab=tasks&focus={task_id}
 *   upcoming milestone  → /entity/{entity_id}?tab=projects&focus={project_id}
 *
 * If required identifiers are missing the function returns `to: null`, and
 * the strip falls back to opening the Right Now drawer.
 */
function computeLead({
  overdue, overdueProjects, atRiskProjects, decisions,
  entityWarnings, dedupedProjectWarnings, upcoming, entityName,
}) {
  const linkTo = (entityId, tab, focusId) => {
    if (!entityId) return null;
    const qs = new URLSearchParams({ tab });
    if (focusId) qs.set("focus", focusId);
    return `/entity/${entityId}?${qs.toString()}`;
  };

  // 1. Overdue task (High/Critical first).
  const highOverdue = overdue.find((t) => ["High", "Critical", "Urgent"].includes(t.priority));
  const topOverdue = highOverdue || overdue[0];
  if (topOverdue) {
    return {
      title: topOverdue.name,
      tag: `overdue · due ${topOverdue.due_date}`,
      color: "#EC4899",
      Icon: AlertCircle,
      kind: "task",
      to: linkTo(topOverdue.entity_id, "tasks", topOverdue.id),
    };
  }
  // 2. Overdue project.
  if (overdueProjects[0]) {
    const p = overdueProjects[0];
    return {
      title: `${p.entity_name ? p.entity_name + " · " : ""}${p.project_name}`,
      tag: "overdue project",
      color: "#EF4444",
      Icon: Activity,
      kind: "project_overdue",
      to: linkTo(p.entity_id, "projects", p.project_id),
    };
  }
  // 3. At-Risk project.
  if (atRiskProjects[0]) {
    const p = atRiskProjects[0];
    return {
      title: `${p.entity_name ? p.entity_name + " · " : ""}${p.project_name}`,
      tag: "at risk",
      color: "#F97316",
      Icon: Activity,
      kind: "project_at_risk",
      to: linkTo(p.entity_id, "projects", p.project_id),
    };
  }
  // 4. Decision.
  if (decisions[0]) {
    const d = decisions[0];
    return {
      title: d.title,
      tag: "decision required",
      color: "#F59E0B",
      Icon: Scale,
      kind: "decision",
      to: linkTo(d.entity_id, "notes", d.id),
    };
  }
  // 5. Budget warning.
  const allBudget = [
    ...entityWarnings.map((w) => ({ ...w, __kind: "entity" })),
    ...dedupedProjectWarnings.map((w) => ({ ...w, __kind: "project" })),
  ].sort((a, b) => Number(b.over === true) - Number(a.over === true) || (b.pct || 0) - (a.pct || 0));
  if (allBudget[0]) {
    const w = allBudget[0];
    const label = w.__kind === "project"
      ? `${w.entity_name || entityName(w.entity_id) || "—"} · ${w.project_name}`
      : (w.entity_name || entityName(w.entity_id) || "Entity");
    return {
      title: label,
      tag: w.over ? `${Math.round(w.pct || 0)}% · over budget` : `${Math.round(w.pct || 0)}% of budget used`,
      color: w.over ? "#EF4444" : "#F59E0B",
      Icon: TrendingDown,
      kind: w.__kind === "project" ? "budget_project" : "budget_entity",
      to: w.__kind === "project"
        ? linkTo(w.entity_id, "budget", w.project_id)
        : linkTo(w.entity_id, "budget"),
    };
  }
  // 6. Upcoming.
  if (upcoming[0]) {
    const u = upcoming[0];
    const when = u.all_day
      ? new Date(u.starts_at).toLocaleDateString([], { month: "short", day: "numeric" })
      : new Date(u.starts_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    // Route by source_type: tasks → tasks tab, project (milestone) → projects,
    // everything else lands on the calendar tab of the owning entity.
    let tab = "calendar";
    if (u.source_type === "task") tab = "tasks";
    else if (u.source_type === "project") tab = "projects";
    return {
      title: u.title,
      tag: `upcoming · ${when}`,
      color: "#3B82F6",
      Icon: CalendarClock,
      kind: `upcoming_${u.source_type || "event"}`,
      to: linkTo(u.entity_id, tab, u.source_id || u.id),
    };
  }
  return null;
}
